import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  setPhoneNumber,
  setTraceId,
  setUser_id,
  settxn_id,
} from "../redux/slice/userSlice";
import SProfileHead from "../components/SProfileHeader";
import CustomButton from "../components/CustomButton";
import CustomTextInput from "../components/CustomInput";
import LogoBetweenLines from "../components/LogoBetweenLines";
import QrifyMark from "../components/site/QrifyMark";
import { ENDPOINT } from "../configuration/configuration";
import { Box, Stack, CircularProgress, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const PhoneNumberScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const validatePhoneNumber = (phoneNumber) => /^\d{10}$/.test(phoneNumber);

  const validateName = (fullName) => {
    const trimmed = fullName.trim();
    return trimmed.length >= 2 && /^[A-Za-z]+(?: [A-Za-z]+)*$/.test(trimmed);
  };

  const handleNameChange = (text) => {
    const sanitized = text.replace(/[^A-Za-z\s]/g, "").slice(0, 50);
    setName(sanitized);

    if (!sanitized.trim()) {
      setNameError("Please enter your name.");
    } else if (!validateName(sanitized)) {
      setNameError("Please enter a valid name (letters only).");
    } else {
      setNameError("");
    }
  };

  const handlePhoneChange = (text) => {
    const sanitized = text.replace(/\D/g, "").slice(0, 10);
    setPhone(sanitized);

    if (!sanitized) {
      setPhoneError("Please enter a valid 10-digit phone number.");
    } else if (!validatePhoneNumber(sanitized)) {
      setPhoneError("Please enter a valid 10-digit phone number.");
    } else {
      setPhoneError("");
    }
  };

  const handleGetOtp = async () => {
    let hasError = false;

    if (!validateName(name)) {
      setNameError(
        name.trim()
          ? "Please enter a valid name (letters only)."
          : "Please enter your name."
      );
      hasError = true;
    } else {
      setNameError("");
    }

    if (!validatePhoneNumber(phone)) {
      setPhoneError("Please enter a valid 10-digit phone number.");
      hasError = true;
    } else {
      setPhoneError("");
    }

    if (hasError) return;

    setLoading(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        const timeoutId = setTimeout(() => {
          setLoading(false);
          alert("Request timed out. Please try again.");
        }, 15000);

        try {
          const response = await fetch(ENDPOINT.Savr(id), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              savr_phone_number: phone,
              savr_name: name.trim(),
              latitude: latitude,
              longitude: longitude,
            }),
          });

          clearTimeout(timeoutId);

          if (!response.ok) {
            if (response.status === 404) {
              alert("User not found.");
            } else {
              alert("Server error occurred. Please try again.");
            }
            throw new Error(`Network response was not ok: ${response.status}`);
          }

          const data = await response.json();

          if (data.status === "success") {
            dispatch(setPhoneNumber(phone));
            dispatch(setTraceId(data.data?.trace_id));
            dispatch(setUser_id(data.data?.ref_id));
            dispatch(settxn_id(data.data?.txnId));
            navigate(`/otp/${id}/${data?.data?.ref_id}`, { replace: true });
          } else {
            alert("Failed to send OTP. Please try again.");
          }
        } catch (error) {
          clearTimeout(timeoutId);
          console.error("Error fetching OTP:", error);
        } finally {
          setLoading(false);
        }
      },
      () => {
        setLoading(false);
        alert("Please enable location access to continue.");
      }
    );
  };

  const handlePrivacyPolicyPress = () => {
    window.open("/privacy", "_blank");
  };

  const handleTermsPress = () => {
    window.open("/terms", "_blank");
  };

  return (
    <Box
      sx={{
        height: "100dvh",
        maxHeight: "100dvh",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: isMobile ? "stretch" : "center",
        background: "linear-gradient(135deg, #f5f7fa, #c3cfe2)",
        overflow: "hidden",
        position: isMobile ? "fixed" : "relative",
        inset: isMobile ? 0 : "auto",
      }}
    >
      <Stack
        sx={{
          width: isMobile ? "100%" : isTablet ? 400 : 360,
          height: isMobile ? "100%" : "min(90dvh, 640px)",
          maxHeight: "100%",
          px: { xs: 2, sm: 2.5 },
          py: { xs: 1.5, sm: 2.5 },
          backgroundColor: "white",
          boxShadow: isMobile ? "none" : "0 8px 16px rgba(0, 0, 0, 0.1)",
          borderRadius: isMobile ? 0 : 2,
          textAlign: "center",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: { xs: 1, sm: 1.5 },
          pb: {
            xs: "max(12px, env(safe-area-inset-bottom))",
            sm: 2.5,
          },
        }}
      >
        <LogoBetweenLines
          logoSource={require("../assets/images/logo.png")}
          height={isMobile ? 40 : 48}
        />

        <SProfileHead
          label1="Greetings 🙏 !"
          label2={
            <>
              Welcome to Svasth-<QrifyMark />
            </>
          }
          size1={isMobile ? 16 : 20}
          size2={isMobile ? 15 : 20}
          color="#303030"
          lineHeight1={isMobile ? 22 : 32}
          lineHeight2={isMobile ? 22 : 32}
        />

        <Box
          component="p"
          sx={{
            m: 0,
            px: 0.5,
            fontSize: { xs: 13, sm: 14 },
            lineHeight: 1.4,
            color: "#666",
          }}
        >
          <strong>Svasth-<QrifyMark /></strong> thanks you for being a Lifesavr.
          Please enter your <strong>Name</strong> and{" "}
          <strong>Mobile Number</strong> to help the victim.
        </Box>

        <Stack spacing={0.75} sx={{ width: "100%", mt: 0.5 }}>
          <CustomTextInput
            label="Name"
            placeholder="Full Name"
            onChangeText={handleNameChange}
            value={name}
            error={nameError}
            maxlength={50}
            autoComplete="off"
          />
          <CustomTextInput
            label="Phone Number"
            placeholder="Phone Number"
            onChangeText={handlePhoneChange}
            value={phone}
            error={phoneError}
            maxlength={10}
            autoComplete="off"
            inputMode="numeric"
            type="tel"
          />
        </Stack>

        <Box sx={{ width: "100%", mt: 0.5 }}>
          {loading ? (
            <Box
              sx={{
                height: 50,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : (
            <CustomButton
              label="Get Otp"
              onClick={handleGetOtp}
              disabled={loading}
            />
          )}
        </Box>

        <Box
          component="p"
          sx={{
            m: 0,
            textAlign: "center",
            color: "#888",
            fontSize: { xs: "0.75rem", sm: "0.8rem" },
            lineHeight: 1.35,
          }}
        >
          By continuing you agree to our{" "}
          <Box
            component="span"
            sx={{
              color: "#1a237e",
              textDecoration: "underline",
              cursor: "pointer",
            }}
            onClick={handleTermsPress}
          >
            Terms & Conditions
          </Box>{" "}
          &{" "}
          <Box
            component="span"
            sx={{
              color: "#1a237e",
              textDecoration: "underline",
              cursor: "pointer",
            }}
            onClick={handlePrivacyPolicyPress}
          >
            Privacy Policy
          </Box>
          .
        </Box>
      </Stack>
    </Box>
  );
};

export default PhoneNumberScreen;
